import React, { useState, useEffect } from 'react';
import { client, urlFor } from '../../client'; // Assuming client setup
import { PortableText } from '@portabletext/react';

// Placeholder for loading state (Consider moving to shared components)
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#2a4865]"></div> 
    <p className="ml-4 text-lg text-gray-700">Loading...</p>
  </div>
);

// Placeholder for error state (Consider moving to shared components)
const ErrorMessage = ({ message }) => (
  <div className="container mx-auto px-4 py-12 text-center">
    <p className="text-red-600 font-semibold">{message || 'Error loading page content. Please try again later.'}</p>
  </div>
);

const PhysicalEducation = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('welcome');

  useEffect(() => {
    // Query to fetch Physical Education page content
    const query = `*[_type == "physicalEducationPage" && _id == "physicalEducationPageContent"][0]{
      bannerTitle,
      bannerImage {
        asset->{
          _id,
          url
        }
      },
      tabs[]{
        _key,
        tabTitle,
        tabIdentifier,
        contentSections[]{
          _key,
          title,
          body
        }
      },
      newsletterTitle,
      newsletterSubtitle,
      newsletterTeacherName,
      newsletterContent
    }`;

    setLoading(true);
    client.fetch(query)
      .then((data) => {
        if (data) {
          setPageData(data);
        } else {
          setError('Physical Education Page content not found.');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch Physical Education page data:', err);
        setError('Failed to load page content. Please try again later.');
        setLoading(false);
      });
  }, []);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;
  if (!pageData) return <ErrorMessage message="No content available for this page." />;

  return (
    <div className="bg-white">
      {/* Hero Banner with Image Overlay - Dynamic */}
      <section className="relative h-[40vh] max-h-[400px]">
        <div className="w-full h-full relative overflow-hidden">
          {pageData.bannerImage?.asset?.url ? (
            <img 
              src={urlFor(pageData.bannerImage).url()} 
              alt={pageData.bannerTitle || 'PS52 Physical Education Program'} 
              className="absolute inset-0 w-full h-full object-cover object-center" 
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0 w-full h-full bg-gray-300"></div> // Placeholder background
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20"></div>
        </div>
        
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl pb-12 md:pb-16 lg:pb-16 text-left">
              <div className="w-16 h-1 bg-yellow-500 mb-6"></div>
              
              <h1 className="text-white text-4xl md:text-5xl font-bold mb-2 leading-tight text-left">
                {pageData.bannerTitle || 'Physical Education'} {/* Dynamic Title */}
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-6xl mx-auto">
            
            {/* Tabs */}
            <div className="flex overflow-x-auto pb-2 mb-8">
              {pageData.tabs && pageData.tabs.length > 0 && (
                <button
                  className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                    activeTab === 'welcome'
                      ? 'bg-[#691e31] text-white shadow-md'
                      : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                  }`}
                  onClick={() => setActiveTab('welcome')}
                >
                  {pageData.tabs[0].tabTitle}
                </button>
              )}
              <button
                className={`py-3 px-6 font-bold rounded-full mr-4 transition-colors whitespace-nowrap ${
                  activeTab === 'newsletter'
                    ? 'bg-[#691e31] text-white shadow-md'
                    : 'bg-[#2a4865]/10 text-[#2a4865] hover:bg-[#2a4865]/20'
                }`}
                onClick={() => setActiveTab('newsletter')}
              >
                {pageData.newsletterTitle || 'Monthly Newsletter'}
              </button>
            </div>

            {/* Welcome Tab Content */}
            {activeTab === 'welcome' && (
              <div className="space-y-6">
                {pageData.tabs && pageData.tabs.length > 0 && pageData.tabs[0].contentSections ? (
                  pageData.tabs[0].contentSections.map((section) => (
                    <section key={section._key} className="bg-[#2a4865]/5 p-5 rounded-lg border-l-4 border-[#2a4865]">
                      <h3 className="text-xl font-bold text-[#2a4865] mb-3 relative after:content-[''] after:absolute after:w-10 after:h-0.5 after:bg-yellow-500 after:-bottom-1 after:left-0">
                        {section.title}
                      </h3>
                      <div className="prose prose-sm sm:prose max-w-none text-gray-700">
                        {section.body && <PortableText value={section.body} />} 
                      </div>
                    </section>
                  ))
                ) : (
                  <p>No content sections available.</p>
                )}
              </div>
            )}

            {/* Newsletter Tab Content */}
            {activeTab === 'newsletter' && (
              <div className="space-y-6">
                <section className="bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="bg-[#2a4865] text-white p-6 rounded-t-lg">
                    <h3 className="text-2xl font-bold mb-2 text-white">
                      {pageData.newsletterSubtitle || 'January 2025 Newsletter'}
                    </h3>
                    <p className="text-white/80 text-sm">
                      Physical Education • {pageData.newsletterTeacherName || 'Coach Martinez'}
                    </p>
                  </div>
                  
                  <div className="p-6 prose prose-lg max-w-none">
                    {pageData.newsletterContent ? (
                      <PortableText value={pageData.newsletterContent} />
                    ) : (
                      <div>
                        <h2 className="text-xl font-bold text-[#691e31] mb-4">Get Moving in the New Year! 🏃‍♂️</h2>
                        
                        <p className="text-gray-700 mb-6">
                          Happy New Year! We're excited to keep our bodies active and healthy through fun physical activities. 
                          This month we're focusing on building strength, endurance, and teamwork skills!
                        </p>

                        <h2 className="text-xl font-bold text-[#691e31] mb-4">This Month's Activities ⚽</h2>
                        
                        <p className="text-gray-700 mb-3">
                          <strong>Indoor Sports:</strong> Basketball skills, floor hockey, and cooperative games 
                          to keep us active during the winter months.
                        </p>
                        
                        <p className="text-gray-700 mb-3">
                          <strong>Fitness Challenges:</strong> Age-appropriate fitness testing and personal goal setting 
                          to help students track their progress and celebrate improvements.
                        </p>
                        
                        <p className="text-gray-700 mb-6">
                          <strong>Team Building:</strong> Activities that promote cooperation, communication, 
                          and good sportsmanship among classmates.
                        </p>

                        <h2 className="text-xl font-bold text-[#691e31] mb-4">Important Dates 📅</h2>
                        
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                          <li><strong>January 15:</strong> MLK Day - No School</li>
                          <li><strong>January 22:</strong> Jump Rope for Heart Kickoff</li>
                          <li><strong>January 29:</strong> Basketball Skills Assessment</li>
                          <li><strong>February 5:</strong> Winter Olympic Games (Indoor)</li>
                        </ul>

                        <h2 className="text-xl font-bold text-[#691e31] mb-4">Staying Active at Home 🏠</h2>
                        
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-6">
                          <li>Encourage 60 minutes of physical activity daily</li>
                          <li>Try family walks, bike rides, or dancing together</li>
                          <li>Practice skills like jumping, hopping, and balancing</li>
                          <li>Limit screen time and promote active play</li>
                          <li>Make physical activity fun, not competitive</li>
                        </ul>

                        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
                          <h3 className="font-bold text-[#691e31] mb-2">📧 Let's Stay Active Together!</h3>
                          <p className="text-gray-700 text-sm">
                            Physical activity is crucial for children's development, both physically and mentally. 
                            Please let me know if you have any questions about your child's PE experience!
                          </p>
                          <p className="text-gray-700 text-sm mt-2">
                            <strong>Keep moving and having fun!</strong><br/>
                            <em>- {pageData.newsletterTeacherName || 'Coach Martinez'}, Physical Education Teacher</em>
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PhysicalEducation; 